import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Edit3, Save, Loader2, UserPlus, UserMinus, Mail, GraduationCap, Heart } from "lucide-react";
import BadgesShelf from "../components/BadgesShelf";

const COLORS = ["#F5A623", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

export default function Profile() {
  const { userId } = useParams();
  const { user: me, refresh } = useAuth();
  const [target, setTarget] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [friends, setFriends] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const isMe = !userId || userId === me?.id;

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data.subjects));
    api.get("/friends").then((r) => setFriends(r.data.map((u) => u.id)));
    if (isMe) { setTarget(me); }
    else { api.get(`/users/${userId}`).then((r) => setTarget(r.data)).catch(() => {}); }
  }, [userId, me, isMe]);

  useEffect(() => {
    if (target) setForm({ ...target });
  }, [target]);

  if (!target || !form) return <div className="p-10 text-center text-[color:var(--bee-muted)]">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        name: form.name, bio: form.bio, school: form.school, year: form.year,
        subjects: form.subjects, avatar_color: form.avatar_color,
      });
      setTarget(data);
      await refresh();
      setEditing(false);
      toast.success("Profile saved");
    } finally { setSaving(false); }
  };

  const toggleSubj = (slug) => {
    setForm((f) => ({
      ...f,
      subjects: f.subjects.includes(slug) ? f.subjects.filter((x) => x !== slug) : [...f.subjects, slug],
    }));
  };

  const toggleFriend = async () => {
    const isFriend = friends.includes(target.id);
    if (isFriend) { await api.delete(`/friends/${target.id}`); setFriends(friends.filter((x) => x !== target.id)); toast("Removed from friends"); }
    else { await api.post(`/friends/${target.id}`); setFriends([...friends, target.id]); toast.success("Added to friends"); }
  };

  const shownSubjects = subjects.filter((s) => target.subjects.includes(s.slug));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 bee-card p-8">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 hex-clip flex items-center justify-center font-display text-4xl font-bold text-white flex-shrink-0"
                 style={{ background: form.avatar_color }}>
              {target.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" className="bee-input font-display text-3xl font-bold w-full" />
              ) : (
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{target.name}</h1>
              )}
              <div className="text-sm text-[color:var(--bee-muted)] mt-1 flex items-center gap-1"><Mail size={14} /> {target.email}</div>
              {(target.school || editing) && (
                <div className="text-sm text-[color:var(--bee-muted)] mt-1 flex items-center gap-1">
                  <GraduationCap size={14} />
                  {editing ? <input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="profile-school" placeholder="School / University" className="bee-input py-1 text-sm" /> : target.school}
                </div>
              )}
            </div>
            {isMe ? (
              editing ? (
                <button onClick={save} disabled={saving} data-testid="save-profile" className="bee-btn-primary flex items-center gap-2">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                </button>
              ) : (
                <button onClick={() => setEditing(true)} data-testid="edit-profile" className="bee-btn-secondary flex items-center gap-2"><Edit3 size={14} /> Edit</button>
              )
            ) : (
              <button onClick={toggleFriend} data-testid="toggle-friend" className={friends.includes(target.id) ? "bee-btn-secondary flex items-center gap-2" : "bee-btn-primary flex items-center gap-2"}>
                {friends.includes(target.id) ? <><UserMinus size={14} /> Following</> : <><UserPlus size={14} /> Add friend</>}
              </button>
            )}
          </div>

          <div className="mt-6">
            <div className="text-xs tracking-[0.1em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Bio</div>
            {editing ? (
              <textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} data-testid="profile-bio" placeholder="Tell others about you..." className="bee-input w-full resize-none" />
            ) : (
              <p className="text-[color:var(--bee-text)] leading-relaxed">{target.bio || <span className="text-[color:var(--bee-muted)] italic">No bio yet.</span>}</p>
            )}
          </div>

          {editing && (
            <div className="mt-6">
              <div className="text-xs tracking-[0.1em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Year / Level</div>
              <input value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="profile-year" placeholder="e.g. Grade 11, Sophomore, Year 2 MSc" className="bee-input w-full" />
            </div>
          )}

          {editing && (
            <div className="mt-6">
              <div className="text-xs tracking-[0.1em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Avatar colour</div>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, avatar_color: c })} data-testid={`color-${c}`}
                    className={`w-8 h-8 hex-clip ${form.avatar_color === c ? "ring-2 ring-offset-2 ring-[color:var(--bee-text)]" : ""}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="text-xs tracking-[0.1em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Subjects ({shownSubjects.length})</div>
            {!editing ? (
              <div className="flex flex-wrap gap-2">
                {shownSubjects.length === 0 && <span className="text-[color:var(--bee-muted)] text-sm italic">No subjects picked.</span>}
                {shownSubjects.map((s) => (
                  <Link key={s.slug} to={`/subjects/${s.slug}`} className="bee-chip hover:bg-[color:var(--bee-primary)]">{s.name}</Link>
                ))}
              </div>
            ) : (
              <div className="max-h-[280px] overflow-y-auto scrollbar-thin border border-[color:var(--bee-border)] p-3 grid sm:grid-cols-2 gap-1" data-testid="subject-picker">
                {subjects.map((s) => (
                  <label key={s.slug} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-[color:var(--bee-surface-alt)]">
                    <input type="checkbox" checked={form.subjects.includes(s.slug)} onChange={() => toggleSubj(s.slug)} className="accent-[color:var(--bee-primary)]" />
                    <span>{s.name}</span>
                    <span className="text-[10px] text-[color:var(--bee-muted)] ml-auto">{s.level === "highschool" ? "HS" : "Uni"}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Side */}
        <div className="space-y-4">
          <div className="bee-card p-5">
            <BadgesShelf earned={target.earned_badges || []} showLocked={isMe} />
          </div>
          {(target.total_donated_cents || 0) > 0 && (
            <div className="bee-card p-5 flex items-center gap-3">
              <Heart size={18} className="text-[color:var(--bee-primary-hover)] fill-current flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-[color:var(--bee-muted)]">Supporter</div>
                <div className="text-sm">Donated <b>${((target.total_donated_cents || 0) / 100).toFixed(0)}</b> to FocusBee</div>
              </div>
            </div>
          )}
          <div className="bee-card p-5">
            <h3 className="font-display font-semibold mb-3">Year</h3>
            <p className="text-sm text-[color:var(--bee-muted)]">{target.year || "Not set"}</p>
          </div>
          <div className="bee-card p-5">
            <h3 className="font-display font-semibold mb-3">Joined</h3>
            <p className="text-sm text-[color:var(--bee-muted)]">
              {new Date(target.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
